import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth, useFirebase } from './AuthContext';
import { doc, onSnapshot } from 'firebase/firestore';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({ children }) => {
    const { user } = useAuth();
    const { db } = useFirebase();
    const [subscription, setSubscription] = useState(null);

    useEffect(() => {
        // Clear subscription if user logs out or is not present
        if (!user || user.isAnonymous || !db) {
            // Set a default "free tier" state
            setSubscription({ isActive: false, status: 'inactive' });
            return;
        }

        // The customer document in Firestore has the same ID as the user's UID
        const customerRef = doc(db, 'customers', user.uid);

        // onSnapshot creates a REAL-TIME listener that watches for any changes to the document
        const unsubscribe = onSnapshot(customerRef, (docSnap) => {
            if (docSnap.exists()) {
                const customerData = docSnap.data();
                // Create a clean subscription object for the rest of the app
                const subData = {
                    isActive: customerData.status === 'active', // Convert status string to a boolean
                    status: customerData.status,
                    priceId: customerData.priceId,
                    subscriptionId: customerData.subscriptionId,
                };
                setSubscription(subData);
            } else {
                // If the customer document doesn't exist yet, they are on the free tier
                setSubscription({ isActive: false, status: 'inactive' });
            }
        });

        // This is a crucial cleanup function that runs when the component unmounts (e.g., user logs out).
        // It detaches the listener to prevent memory leaks.
        return () => unsubscribe();

    }, [user, db]); // This effect re-runs whenever the user or db object changes

    return (
        <SubscriptionContext.Provider value={{ subscription }}>
            {children}
        </SubscriptionContext.Provider>
    );
};